import api, { ApiResponse } from './api';
import {
  CreditLink,
  CreditEntry,
  CreditValidation,
  CreateCreditLinkProps,
  CreateCreditEntryProps,
} from 'entities/Credit';

interface Resp<T> {
  success: boolean;
  data: T;
}

// ─── Links ───────────────────────────────────────────────────────────────────

export const createCreditLinkService = (
  data: CreateCreditLinkProps
): ApiResponse<Resp<CreditLink>> =>
  api({ method: 'POST', url: 'credits/links', data, useToken: true });

export const updateCreditLinkService = (
  id: string,
  data: Partial<CreateCreditLinkProps>
): ApiResponse<Resp<CreditLink>> =>
  api({ method: 'PATCH', url: `credits/links/${id}`, data, useToken: true });

export const deleteCreditLinkService = (id: string): ApiResponse<Resp<void>> =>
  api({ method: 'DELETE', url: `credits/links/${id}`, useToken: true });

export const getLinksByGeneratorService = (
  consumerUnitId: string
): ApiResponse<Resp<CreditLink[]>> =>
  api({
    method: 'GET',
    url: `credits/consumer-units/${consumerUnitId}/links-as-generator`,
    useToken: true,
  });

export const getLinksByReceiverService = (
  consumerUnitId: string
): ApiResponse<Resp<CreditLink[]>> =>
  api({
    method: 'GET',
    url: `credits/consumer-units/${consumerUnitId}/links-as-receiver`,
    useToken: true,
  });

// ─── Entries ─────────────────────────────────────────────────────────────────

export const getCreditEntriesService = (
  consumerUnitId: string
): ApiResponse<Resp<CreditEntry[]>> =>
  api({
    method: 'GET',
    url: `credits/consumer-units/${consumerUnitId}/entries`,
    useToken: true,
  });

export const createCreditEntryService = (
  data: CreateCreditEntryProps
): ApiResponse<Resp<CreditEntry>> =>
  api({ method: 'POST', url: 'credits/entries', data, useToken: true });

export const updateCreditEntryService = (
  id: string,
  data: Partial<CreateCreditEntryProps>
): ApiResponse<Resp<CreditEntry>> =>
  api({ method: 'PATCH', url: `credits/entries/${id}`, data, useToken: true });

export const deleteCreditEntryService = (id: string): ApiResponse<Resp<void>> =>
  api({ method: 'DELETE', url: `credits/entries/${id}`, useToken: true });

// ─── Validação ───────────────────────────────────────────────────────────────

export const validateCreditsService = (
  consumerUnitId: string
): ApiResponse<Resp<CreditValidation[]>> =>
  api({
    method: 'GET',
    url: `credits/consumer-units/${consumerUnitId}/validate`,
    useToken: true,
  });

// ─── Otimizador do rateio (out/2026 — admin-only) ─────────────────────────────
// Espelho de backend/src/domain/entities/RateioOptimization.ts. Uma análise por
// distribuidora com UG; kWh em FP-equivalente; percentuais inteiros.

export type CreditSharingType =
  | 'consorcio'
  | 'cooperativa'
  | 'associacao_civil';

export const CREDIT_SHARING_LABELS: Record<CreditSharingType, string> = {
  consorcio: 'Consórcio',
  cooperativa: 'Cooperativa',
  // chave = valor gravado pela API (snake_case de propósito)
  // eslint-disable-next-line camelcase
  associacao_civil: 'Associação civil',
};

export interface RateioSimulationView {
  usedKwh: number;
  uncoveredKwh: number;
  valueBrl: number;
  endBalanceKwh: number;
}

export interface RateioUnitView {
  consumerUnitId: string;
  name: string;
  installation: string;
  subgroupName: string;
  tierRank: number;
  tierLabel: string;
  hasFv: boolean;
  isGenerator: boolean;
  eligible: boolean;
  eligibleFrom: string[];
  ineligibleReason: 'other_holder' | 'missing_document' | null;
  creditSharingType: CreditSharingType | null;
  balanceKwh: number;
  balanceTrendKwhMonth: number | null;
  projectedNeedKwhMonth: number;
  observedNeedKwhMonth: number | null;
  needKwhMonth: number;
  bufferKwh: number;
  targetKwhMonth: number;
  valuePerKwh: number | null; // R$/kWh médio da curva de benefício
  // Curva: kWh FP-eq que o crédito ainda abate por posto (FP → Int → Ponta) e o R$/kWh de cada faixa
  valueSegments: { posto: 'fp' | 'int' | 'p'; kwh: number; value: number }[];
  autonomyMonths: number | null;
  currentPct: Record<string, number>;
  proposedPct: Record<string, number>;
  simulation: { current: RateioSimulationView; proposed: RateioSimulationView };
}

export interface RateioGeneratorView {
  consumerUnitId: string;
  name: string;
  monthsAnalyzed: number;
  monthlySurplusKwh: number;
  gdRule: 'GDI' | 'GDII' | 'GDIII' | null;
  currentRetainedPct: number;
  proposedRetainedPct: number;
  leftoverRecipientId: string | null;
  priorityOrder: string[];
  changed: boolean;
  locked: boolean; // alteração pedida há menos de 3 meses: fica com o rateio atual
  // Alteração já solicitada: o "% atual" é o de antes do pedido e o proposto,
  // o contratado (scheduledFrom = mês em que passa a valer)
  scheduledChange: boolean;
  scheduledFrom: string | null;
  effectiveFrom: string;
  lastRequestAt: string | null;
  lastRequestId: string | null; // última "Alteração do Rateio FV" — abre o Anexo G.2
  nextChangeAvailableAt: string | null;
  canApply: boolean;
  reason?: string;
}

export interface RateioOptimization {
  utilityCompanyId: string;
  utilityCompanyName: string;
  horizonMonths: number;
  effectiveFrom: string;
  generators: RateioGeneratorView[];
  units: RateioUnitView[];
  annualValueCurrentBrl: number;
  annualValueProposedBrl: number;
  estimatedMonthlySavings: number;
  optimized: boolean;
  reason?: string;
  notes: string[];
  generatedAt: string;
}

export interface ApplyRateioProps {
  allocations: { consumerUnitId: string; percentage: number }[];
  priorityOrder?: string[];
  estimatedMonthlySavings?: number;
}

export const getRateioAnalysisService = (): ApiResponse<
  Resp<RateioOptimization[]>
> => api({ method: 'GET', url: 'credits/rateio-analysis', useToken: true });

export const applyRateioService = (
  generatingUnitId: string,
  data: ApplyRateioProps
): ApiResponse<
  Resp<{
    effectiveFrom: string;
    nextChangeAvailableAt: string;
    serviceRequestId: string;
  }>
> =>
  api({
    method: 'POST',
    url: `credits/rateio-analysis/${generatingUnitId}/apply`,
    data,
    useToken: true,
  });

export const setCreditSharingService = (
  consumerUnitId: string,
  creditSharingType: CreditSharingType | null
): ApiResponse<Resp<void>> =>
  api({
    method: 'PATCH',
    url: `credits/consumer-units/${consumerUnitId}/credit-sharing`,
    data: { creditSharingType },
    useToken: true,
  });
